import { Sparkles, ExternalLink } from "lucide-react";
import { resolveCoverUrl } from "@/lib/api";

export default function FeaturedSpotlight({ book, onOpenDetail }) {
  if (!book) return null;
  const coverUrl = resolveCoverUrl(book.cover_image_url);

  return (
    <section
      className="relative max-w-7xl mx-auto px-4 sm:px-6 md:px-8 mt-2 mb-8"
      data-testid="featured-spotlight"
      aria-label="Featured book of the month"
    >
      <div className="relative overflow-hidden rounded-[28px] bg-gradient-to-br from-[#FFF8E7] via-white to-[#E6F5FA] border-2 border-[#F7C94B] shadow-[0_25px_55px_-15px_rgba(247,201,75,0.45)]">
        {/* Decorative sparkle backdrop */}
        <div
          aria-hidden="true"
          className="absolute -top-12 -right-10 w-64 h-64 rounded-full bg-[#F7C94B]/25 blur-3xl"
        />
        <div
          aria-hidden="true"
          className="absolute -bottom-12 -left-10 w-64 h-64 rounded-full bg-[#38B6D2]/20 blur-3xl"
        />

        <div className="relative grid md:grid-cols-[auto_1fr] gap-7 md:gap-10 p-6 md:p-10 items-center">
          {/* Cover */}
          <button
            type="button"
            onClick={() => onOpenDetail?.(book)}
            aria-label={`View details for ${book.title}`}
            className="group relative block mx-auto md:mx-0 w-[170px] md:w-[200px] aspect-[2/3] rounded-md overflow-hidden shadow-[0_18px_40px_-10px_rgba(14,35,46,0.45)] border-l-4 border-black/25 border-r border-black/15 transition-transform hover:scale-[1.03] focus:outline-none focus:ring-4 focus:ring-[#F7C94B]/60"
            style={{
              backgroundColor: "#0E232E",
              backgroundImage: coverUrl ? `url(${coverUrl})` : undefined,
              backgroundSize: "cover",
              backgroundPosition: "center",
            }}
            data-testid="featured-cover"
          >
            {!coverUrl && (
              <span className="absolute inset-0 flex items-center justify-center p-3 text-center text-white font-bold text-sm">
                {book.title}
              </span>
            )}
            {/* Ribbon */}
            <span className="absolute top-3 -left-1 bg-[#F7C94B] text-[#0E232E] text-[10px] font-extrabold uppercase tracking-wider px-3 py-1 shadow-md rotate-[-4deg] border border-[#D4A82E]">
              ★ Featured
            </span>
          </button>

          {/* Details */}
          <div className="text-center md:text-left">
            <div className="inline-flex items-center gap-2 text-[#2B328C] font-extrabold text-[10px] md:text-[11px] uppercase tracking-[0.22em]">
              <Sparkles size={14} className="text-[#F7C94B]" />
              Book of the Month
            </div>
            <h2
              className="mt-2 font-display text-3xl md:text-4xl lg:text-5xl font-extrabold text-[#0E232E] leading-[1.05] tracking-tight"
              data-testid="featured-title"
            >
              {book.title}
            </h2>
            <p
              className="mt-2 text-sm md:text-base text-[#2F2E2E] font-semibold"
              data-testid="featured-author"
            >
              by {book.author_name}
            </p>
            <span className="inline-block mt-3 text-[10px] font-semibold uppercase tracking-wider bg-white text-[#0E232E] px-2.5 py-1 rounded border border-[#CFD6DC]">
              {book.age_group_label}
            </span>
            <p className="mt-4 text-[14px] md:text-[15px] text-[#2F2E2E] leading-relaxed line-clamp-4 md:line-clamp-3 max-w-2xl">
              {book.description}
            </p>

            <div className="mt-5 flex flex-wrap items-center justify-center md:justify-start gap-3">
              <a
                href={book.purchase_url}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary px-5 py-2.5 rounded-full text-sm font-bold inline-flex items-center gap-2"
                data-testid="featured-buy-btn"
              >
                Get this book <ExternalLink size={14} />
              </a>
              <button
                type="button"
                onClick={() => onOpenDetail?.(book)}
                className="px-5 py-2.5 rounded-full text-sm font-bold border-2 border-[#0E232E] text-[#0E232E] hover:bg-[#0E232E] hover:text-white transition-colors"
                data-testid="featured-learn-more"
              >
                Read more
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
